import { Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { VEILLE_COMMERCIALE } from "@/config/siteFlags";

const LINKEDIN_URL = "https://www.linkedin.com/in/simon-marie/";

const TRANSVERSAL = [
  { t: "Protection des données personnelles", d: "Les droits et obligations qui encadrent la collecte, la diffusion et la conservation des informations relatives aux personnes." },
  { t: "Exposition et circulation de l'information", d: "La manière dont une donnée publiée dans un contexte donné est reprise, agrégée, enrichie puis rendue plus visible qu'à l'origine." },
  { t: "Analyse juridique et documentaire", d: "L'examen des sources, de leur licéité, de leur finalité et des démarches qui peuvent réellement être envisagées." },
  { t: "Mécanismes d'indexation et de réutilisation", d: "Le fonctionnement des moteurs de recherche, des annuaires, des agrégateurs et des rediffuseurs de données." },
];

const BIO = [
  "Simon Marie développe HorsIndex depuis Boulogne-sur-Mer autour des questions de protection des données personnelles, de conformité et de réduction de l'exposition numérique. Le projet part d'une question simple en apparence : que peut réellement faire une personne lorsque des informations la concernant restent accessibles, diffusées ou indexées en ligne ?",
  "Son parcours associe le droit, l'économie et la gestion, avec un intérêt particulier pour les questions de conformité, de protection des données personnelles et de circulation de l'information dans les environnements numériques. Cette formation transversale l'amène à aborder les situations moins sous l'angle d'une procédure unique que comme des problèmes qui nécessitent d'identifier les différents acteurs, les différentes sources et les différents mécanismes de diffusion concernés.",
  "Une information visible dans un moteur de recherche peut en effet provenir d'un registre public, d'un annuaire privé, d'un site d'information, d'une plateforme commerciale ou d'une reprise automatisée de données. Sa suppression de la source, sa rectification, son déréférencement ou la limitation de sa diffusion répondent à des logiques différentes. L'un des principes de HorsIndex consiste précisément à ne pas confondre ces différents niveaux.",
  "Simon Marie travaille ainsi à la croisée de l'analyse documentaire, de la protection des données et de la compréhension des écosystèmes numériques. Il s'intéresse notamment à la manière dont une donnée initialement publiée dans un contexte déterminé peut être copiée, agrégée, enrichie, réindexée puis rendue beaucoup plus visible que ne le justifiait sa publication d'origine.",
  "Cette approche implique également de distinguer ce qui est techniquement possible de ce qui peut juridiquement être demandé. Une information peut être licitement publiée sans pour autant devoir bénéficier indéfiniment de la même visibilité. À l'inverse, le simple souhait de voir disparaître une donnée ne suffit pas nécessairement à créer un droit à son effacement. L'analyse doit donc tenir compte de la nature de l'information, de sa source, de son ancienneté, de sa pertinence actuelle, des droits des personnes concernées et, lorsqu'il existe, de l'intérêt du public à y avoir accès.",
  "En parallèle de HorsIndex, Simon Marie exerce des activités de conseil et d'accompagnement auprès de professionnels et d'organisations. Il poursuit également un travail de formation dans le domaine de la conformité internationale, afin d'approfondir les dimensions juridiques et organisationnelles qui entourent la gouvernance de l'information et des données.",
];

const PROFILE_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  url: "https://horsindex.com/simon-marie/",
  mainEntity: {
    "@type": "Person",
    name: "Simon Marie",
    url: "https://horsindex.com/simon-marie/",
    image: "https://horsindex.com/simon-marie.jpg",
    description:
      "Simon Marie, à Boulogne-sur-Mer, développe HorsIndex autour de la protection des données personnelles, de la conformité et de la réduction de l'exposition numérique.",
    homeLocation: { "@type": "Place", name: "Boulogne-sur-Mer, France" },
    worksFor: {
      "@type": "Organization",
      name: "HorsIndex",
      url: "https://horsindex.com/",
    },
    sameAs: [LINKEDIN_URL],
  },
}).replace(/</g, "\\u003c");

export default function SimonMarie() {
  return (
    <PageLayout
      eyebrow="À propos"
      title="Simon Marie"
      intro="À l'origine de HorsIndex. Comprendre l'exposition avant d'agir, distinguer les démarches disponibles et utiliser celle qui correspond réellement à la situation : c'est l'approche qui guide son travail."
      ctaTitle={VEILLE_COMMERCIALE ? "Échanger avec Simon Marie" : "Une information vous concernant reste visible en ligne ?"}
      ctaButton={VEILLE_COMMERCIALE ? "Prendre contact" : "Présenter une situation"}
      ctaTo="/contact"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: PROFILE_LD }}
      />
      {/* Parcours et approche */}
      <section data-testid="sm-parcours" className="bg-[#FAF9F7] py-20 lg:py-28 border-t border-[#E2DEDA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="label-eyebrow mb-6">Parcours et approche</p>
            <img
              src="/simon-marie.jpg"
              alt="Portrait de Simon Marie, à l'origine de HorsIndex"
              width="480"
              height="484"
              loading="lazy"
              data-testid="sm-portrait"
              className="w-full max-w-[340px] rounded-md border border-[#E2DEDA]"
            />
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sm-linkedin-link"
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap text-[13px] font-normal text-[#666A70] hover:text-[#181A1D] underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#181A1D] transition-colors"
            >
              <Linkedin className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" strokeWidth={1.5} />
              Voir le profil LinkedIn
            </a>
          </div>
          <div className="lg:col-span-8 space-y-6 text-[15px] lg:text-base leading-relaxed text-[#52565C] font-normal max-w-3xl">
            {BIO.map((p) => (
              <p key={p.slice(0, 48)}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Approche transversale */}
      <section data-testid="sm-transversal" className="bg-[#F2F0ED] py-20 lg:py-28 border-t border-[#E2DEDA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="label-eyebrow mb-6">Une approche transversale</p>
          <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl tracking-tight leading-[1.08] max-w-3xl text-balance">
            Toute la chaîne de diffusion d'une information, pas seulement Google.
          </h2>
          <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
            {TRANSVERSAL.map((x, i) => (
              <li key={x.t} data-testid={`sm-axe-${i}`} className="border-l border-[#D8D3CD] pl-6 py-1">
                <h3 className="font-serif text-xl text-[#181A1D] font-normal leading-snug mb-3 text-balance">{x.t}</h3>
                <p className="text-sm text-[#666A70] font-normal leading-relaxed text-pretty">{x.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* HorsIndex */}
      <section data-testid="sm-horsindex" className="bg-[#FAF9F7] py-20 lg:py-28 border-t border-[#E2DEDA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="label-eyebrow mb-6">HorsIndex</p>
            <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl tracking-tight leading-[1.08] text-balance">
              L'origine du projet HorsIndex.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-[15px] lg:text-base leading-relaxed text-[#52565C] font-normal max-w-3xl">
            <p>
              HorsIndex est né de ce travail d'analyse appliquée. Le projet sert
              de laboratoire méthodologique pour structurer des démarches souvent
              présentées de manière trop simplifiée sous les termes de
              « suppression » ou de « droit à l'oubli ». Son ouverture
              commerciale est actuellement différée : le site rassemble pour le
              moment une présentation du projet et des ressources éditoriales.
            </p>
            <p>
              Chaque situation est analysée individuellement : la réponse dépend
              notamment de la nature de l'information, de sa source, de sa licéité,
              de son ancienneté, de son intérêt public éventuel et des possibilités
              techniques ou juridiques réellement disponibles. Cette méthode conduit
              à privilégier des analyses ciblées plutôt qu'une multiplication de
              demandes standardisées, selon les cas, auprès de l'éditeur d'un site,
              du responsable d'un traitement, d'un intermédiaire ou d'un moteur de
              recherche.
            </p>
            <p className="pt-2">
              <Link
                to="/contact"
                data-testid="sm-contact-link"
                className="text-[#181A1D] underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#E94B3C] transition-colors"
              >
                Prendre contact
              </Link>
            </p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
